import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api, ApiClientError } from '../../lib/api.js';
import { useListQuery } from '../../hooks/useListQuery.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { DataTable } from '../../components/ui/DataTable.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { TextField, SelectField } from '../../components/ui/Field.jsx';

const STATUS_LABEL = {
  in_stock: 'In stock',
  sold: 'Sold',
  issued: 'Issued',
  in_repair: 'In repair',
  melted: 'Melted',
};

const STATUS_TONE = {
  in_stock: 'text-success',
  sold: 'text-muted',
  issued: 'text-warning',
  in_repair: 'text-info',
  melted: 'text-danger',
};

const COLUMNS = [
  { key: 'item_code', header: 'Code' },
  { key: 'name', header: 'Name', sortKey: 'name' },
  { key: 'category_name', header: 'Category', render: (r) => r.category_name || '—' },
  { key: 'purity_label', header: 'Purity', render: (r) => r.purity_label || '—' },
  {
    key: 'net_weight',
    header: 'Net wt.',
    align: 'right',
    sortKey: 'net_weight',
    render: (r) => `${Number(r.net_weight).toFixed(3)} g`,
  },
  {
    key: 'status',
    header: 'Status',
    render: (r) => <span className={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</span>,
  },
];

const EMPTY_FORM = {
  name: '',
  categoryId: '',
  metal: 'gold',
  purityId: '',
  hsnId: '',
  grossWeight: '',
  netWeight: '',
  stoneWeight: '',
  stoneCharges: '',
  makingType: 'per_gram',
  makingValue: '',
  quantity: 1,
};

export default function ItemsPage() {
  const [query, onQueryChange] = useListQuery();
  const debouncedSearch = useDebouncedValue(query.search, 350);
  const queryClient = useQueryClient();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const listQuery = useQuery({
    queryKey: ['items', { ...query, search: debouncedSearch }],
    queryFn: () => api.get('/items', { query: { ...query, search: debouncedSearch } }),
    placeholderData: (prev) => prev,
  });

  const categoriesQuery = useQuery({ queryKey: ['items', 'categories'], queryFn: () => api.get('/items/categories') });
  const puritiesQuery = useQuery({ queryKey: ['masters', 'purities'], queryFn: () => api.get('/masters/purities') });
  const hsnQuery = useQuery({ queryKey: ['masters', 'hsn'], queryFn: () => api.get('/masters/hsn-codes') });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFieldErrors({});
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name,
      categoryId: '',
      metal: row.metal,
      purityId: '',
      hsnId: '',
      grossWeight: row.gross_weight,
      netWeight: row.net_weight,
      stoneWeight: '',
      stoneCharges: '',
      makingType: row.making_type,
      makingValue: row.making_value,
      quantity: row.quantity,
    });
    setFieldErrors({});
    setFormError(null);
    setModalOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: () => (editing ? api.patch(`/items/${editing.id}`, form) : api.post('/items', form)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      setModalOpen(false);
    },
    onError: (err) => {
      if (err instanceof ApiClientError && err.details) setFieldErrors(err.details);
      else setFormError(err instanceof ApiClientError ? err.message : 'Could not save. Please try again.');
    },
  });

  const onSubmit = (e) => {
    e.preventDefault();
    setFieldErrors({});
    setFormError(null);
    saveMutation.mutate();
  };

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/items/${editing.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      setConfirmDeleteOpen(false);
      setModalOpen(false);
    },
    onError: (err) => setDeleteError(err instanceof ApiClientError ? err.message : 'Could not delete this item.'),
  });

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Items</h1>
        <Button onClick={openCreate}>
          <Plus size={17} aria-hidden="true" /> Add
        </Button>
      </div>

      <DataTable
        columns={COLUMNS}
        data={listQuery.data?.data ?? []}
        meta={listQuery.data?.meta}
        query={query}
        onQueryChange={onQueryChange}
        isLoading={listQuery.isLoading}
        onRowClick={openEdit}
        searchPlaceholder="Search by name or item code…"
        emptyLabel="No items yet. Add the first one to get started."
      />

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit item' : 'Add item'}>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <TextField label="Name" required value={form.name} onChange={setField('name')} error={fieldErrors.name} />

          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Category" value={form.categoryId} onChange={setField('categoryId')}>
              <option value="">—</option>
              {categoriesQuery.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </SelectField>
            <SelectField label="Metal" value={form.metal} onChange={setField('metal')}>
              <option value="gold">Gold</option>
              <option value="silver">Silver</option>
              <option value="platinum">Platinum</option>
            </SelectField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Purity" value={form.purityId} onChange={setField('purityId')}>
              <option value="">—</option>
              {puritiesQuery.data
                ?.filter((p) => p.metal === form.metal)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
            </SelectField>
            <SelectField label="HSN code" value={form.hsnId} onChange={setField('hsnId')}>
              <option value="">—</option>
              {hsnQuery.data?.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.description} — {h.code} ({h.gst_rate}%)
                </option>
              ))}
            </SelectField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Gross weight (g)"
              type="number"
              inputMode="decimal"
              step="0.001"
              value={form.grossWeight}
              onChange={setField('grossWeight')}
              error={fieldErrors.grossWeight}
            />
            <TextField
              label="Net weight (g)"
              type="number"
              inputMode="decimal"
              step="0.001"
              value={form.netWeight}
              onChange={setField('netWeight')}
              error={fieldErrors.netWeight}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Making charge" value={form.makingType} onChange={setField('makingType')}>
              <option value="per_gram">₹ per gram</option>
              <option value="flat">Flat ₹</option>
            </SelectField>
            <TextField
              label={form.makingType === 'per_gram' ? 'Rate (₹/g)' : 'Amount (₹)'}
              type="number"
              inputMode="decimal"
              step="0.01"
              value={form.makingValue}
              onChange={setField('makingValue')}
              error={fieldErrors.makingValue}
              hint="Rupees only — never a percentage"
            />
          </div>

          <TextField
            label="Quantity"
            type="number"
            inputMode="numeric"
            min="1"
            value={form.quantity}
            onChange={setField('quantity')}
            error={fieldErrors.quantity}
          />

          {formError ? (
            <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
              {formError}
            </p>
          ) : null}

          <div className="mt-2 flex items-center justify-between gap-3">
            {editing && editing.status === 'in_stock' ? (
              <button
                type="button"
                onClick={() => { setDeleteError(null); setConfirmDeleteOpen(true); }}
                className="flex items-center gap-1.5 text-sm text-danger hover:underline"
              >
                <Trash2 size={15} aria-hidden="true" /> Delete
              </button>
            ) : editing ? (
              <span className="text-xs text-muted">
                {STATUS_LABEL[editing.status]} items keep their history and can&rsquo;t be deleted.
              </span>
            ) : (
              <span />
            )}
            <div className="flex gap-3">
              <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={saveMutation.isPending}>
                Save
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      <Modal open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} title="Delete this item?">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            <b>{editing?.name}</b> ({editing?.item_code}) will be removed completely. This can&rsquo;t be undone.
          </p>
          {deleteError ? (
            <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
              {deleteError}
            </p>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setConfirmDeleteOpen(false)}>
              Keep item
            </Button>
            <Button variant="danger" loading={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
              <Trash2 size={16} aria-hidden="true" /> Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
